import { Link } from 'react-router-dom'

export function TermsPage() {
  return (
    <div className="legal-page">
      <div className="legal-page-inner">
        <Link to="/login" className="legal-back-link">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 12L6 8l4-4" /></svg>
          Back to Sign in
        </Link>

        <div className="legal-header">
          <img src="/images/paver-logo.png" alt="Paver" className="legal-logo" />
          <h1>Terms &amp; Conditions</h1>
          <p className="legal-effective">Effective Date: March 17, 2026</p>
        </div>

        <div className="legal-body">
          <p>
            These Terms and Conditions ("Terms") govern your access to and use of the Paver web
            application and related services (the "Service") provided by Paver Technologies, Inc.
            ("Paver," "we," "us," or "our"). By accessing or using the Service, you agree to be
            bound by these Terms. If you do not agree, do not use the Service.
          </p>

          <h2>1. Definitions</h2>
          <p>
            "Account" means the user account created to access the Service. "Tenant" means the
            organization or company that has subscribed to the Service. "User" means any individual
            authorized by a Tenant to access the Service. "Content" means any data, text, images,
            files, or other materials uploaded, created, or transmitted through the Service.
          </p>

          <h2>2. Account Registration and Access</h2>
          <p>
            To use the Service, you must be invited by a Tenant administrator or create an account
            through our onboarding process. You are responsible for maintaining the confidentiality
            of your login credentials and for all activity that occurs under your account. You must
            notify us immediately of any unauthorized use. You represent that the information you
            provide is accurate and current.
          </p>

          <h2>3. Acceptable Use</h2>
          <p>You agree not to:</p>
          <p>
            Use the Service for any unlawful purpose or in violation of any applicable laws or
            regulations; attempt to gain unauthorized access to the Service, other accounts, or
            any related systems or networks; reverse engineer, decompile, disassemble, or otherwise
            attempt to discover the source code of the Service; interfere with or disrupt the
            integrity or performance of the Service; transmit any viruses, malware, or other harmful
            code; use the Service to send unsolicited communications; sublicense, resell, or
            redistribute the Service without our written consent; or use automated tools to scrape
            or extract data from the Service.
          </p>

          <h2>4. Subscription and Payment</h2>
          <p>
            Access to the Service requires an active subscription. Subscription terms, pricing,
            and billing cycles are as described in your order agreement or as displayed at the
            time of purchase. All fees are non-refundable except as expressly stated in your
            agreement. We reserve the right to modify pricing with 30 days' prior notice. Failure
            to pay may result in suspension or termination of access.
          </p>

          <h2>5. Intellectual Property</h2>
          <p>
            The Service, including its design, features, code, documentation, and all related
            intellectual property, is owned by Paver Technologies, Inc. and protected by copyright,
            trademark, and other laws. We grant you a limited, non-exclusive, non-transferable
            license to use the Service for your internal business purposes during your subscription
            term. You retain ownership of all Content you upload or create through the Service.
          </p>

          <h2>6. Your Content</h2>
          <p>
            You retain all rights to the Content you submit through the Service. By uploading
            Content, you grant us a limited license to store, process, display, and transmit that
            Content solely as necessary to provide the Service. You are solely responsible for the
            accuracy, legality, and appropriateness of your Content. We do not monitor or endorse
            user Content.
          </p>

          <h2>7. Data Handling and Privacy</h2>
          <p>
            Our collection and use of personal information is described in our{' '}
            <Link to="/privacy" className="legal-inline-link">Privacy Policy</Link>, which is
            incorporated into these Terms by reference. By using the Service, you acknowledge that
            you have read and understood our Privacy Policy.
          </p>

          <h2>8. Tenant Data Isolation</h2>
          <p>
            The Service operates on a multi-tenant architecture. Each Tenant's data is logically
            isolated and accessible only to authorized Users within that Tenant. We implement
            row-level security and access controls to ensure that one Tenant's data is not
            accessible to another.
          </p>

          <h2>9. Proposals and Digital Signatures</h2>
          <p>
            The Service allows you to generate proposals and collect digital signatures from
            your clients. You acknowledge that the legal enforceability of digital signatures
            may vary by jurisdiction and that you are responsible for ensuring compliance with
            applicable electronic signature laws (such as the ESIGN Act or UETA) in your
            jurisdiction. Paver does not provide legal advice regarding the enforceability of
            signatures collected through the Service.
          </p>

          <h2>10. Third-Party Services</h2>
          <p>
            The Service integrates with third-party providers including Stripe for payment
            processing, Mapbox for mapping and geolocation, Resend for email delivery, and
            Supabase for data storage. Your use of features powered by these providers may
            be subject to their respective terms of service and privacy policies. We are not
            responsible for the practices or availability of third-party services.
          </p>

          <h2>11. Availability and Support</h2>
          <p>
            We strive to maintain high availability of the Service but do not guarantee
            uninterrupted access. We may perform scheduled maintenance, deploy updates, or
            experience downtime due to circumstances beyond our control. We will make
            reasonable efforts to notify you of planned maintenance in advance.
          </p>

          <h2>12. Limitation of Liability</h2>
          <p>
            To the maximum extent permitted by law, Paver and its officers, directors, employees,
            and affiliates shall not be liable for any indirect, incidental, special, consequential,
            or punitive damages, including but not limited to loss of profits, revenue, data, or
            business opportunities, arising from your use of or inability to use the Service,
            regardless of the theory of liability. Our total aggregate liability for any claims
            arising under these Terms shall not exceed the amount you paid to us in the twelve (12)
            months preceding the claim.
          </p>

          <h2>13. Disclaimer of Warranties</h2>
          <p>
            The Service is provided "as is" and "as available" without warranties of any kind,
            whether express, implied, or statutory, including but not limited to implied warranties
            of merchantability, fitness for a particular purpose, and non-infringement. We do not
            warrant that the Service will meet your specific requirements, that cost calculations
            or estimates generated through the Service will be accurate, or that the Service will
            be error-free.
          </p>

          <h2>14. Indemnification</h2>
          <p>
            You agree to indemnify, defend, and hold harmless Paver and its officers, directors,
            employees, and affiliates from any claims, losses, damages, liabilities, and expenses
            (including reasonable attorneys' fees) arising from your use of the Service, your
            violation of these Terms, your Content, or your violation of any rights of a third party.
          </p>

          <h2>15. Termination</h2>
          <p>
            Either party may terminate the subscription in accordance with the applicable order
            agreement. We may suspend or terminate your access immediately if you violate these
            Terms or if your Tenant's subscription expires or is cancelled. Upon termination,
            your right to use the Service ceases immediately. We will make your data available
            for export for 30 days following termination, after which it may be deleted.
          </p>

          <h2>16. Modifications to the Service and Terms</h2>
          <p>
            We reserve the right to modify, update, or discontinue any aspect of the Service at
            any time. We may also update these Terms from time to time. Material changes will be
            communicated via email or in-app notice at least 30 days before taking effect. Your
            continued use of the Service after updated Terms are posted constitutes acceptance.
          </p>

          <h2>17. Governing Law and Disputes</h2>
          <p>
            These Terms are governed by the laws of the State of California, without regard to
            conflict of law principles. Any disputes arising from these Terms or the Service
            shall be resolved through binding arbitration administered by the American Arbitration
            Association in accordance with its Commercial Arbitration Rules, conducted in
            San Francisco, California. Either party may seek injunctive relief in a court of
            competent jurisdiction.
          </p>

          <h2>18. General Provisions</h2>
          <p>
            These Terms, together with our Privacy Policy and any applicable order agreement,
            constitute the entire agreement between you and Paver regarding the Service. If any
            provision of these Terms is found to be unenforceable, the remaining provisions will
            continue in full force. Our failure to enforce any provision does not constitute a
            waiver. You may not assign your rights under these Terms without our prior written
            consent. Paver may assign its rights and obligations without restriction.
          </p>

          <h2>19. Contact Us</h2>
          <p>
            If you have questions about these Terms, please contact us at:
          </p>
          <p>
            Paver Technologies, Inc.<br />
            Email: legal@getpaver.com<br />
            Website: https://getpaver.com
          </p>
        </div>

        <div className="legal-footer-nav">
          <Link to="/privacy">Privacy Policy</Link>
          <span className="legal-footer-sep">|</span>
          <Link to="/login">Sign in</Link>
        </div>
      </div>
    </div>
  )
}
